import { formatPlanningBufferLabel } from '@/utils/planningBuffer';
import { useState, useEffect } from 'react';
import { EFFICIENCY_NAMES } from '@/lib/efficiencyNames'; // CORE-G GAP-005: one name, one meaning
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Plus, Edit, Wrench, Clock, Award } from 'lucide-react';
import { resolveSkillTier, PROFICIENCY_CATEGORIES } from '@/constants/proficiencyCategories';
import { useAuth } from '@/contexts/AuthContext';

const Technicians = () => {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTechnicians = async () => {
      try {
        const { data, error } = await supabase
          .from('technicians')
          .select('*')
          .order('name');

        if (error) throw error;
        if (data) setTechnicians(data);
      } catch (error: any) {
        toast({
          title: 'Error loading technicians',
          description: error.message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadTechnicians();

    // R36: Subscribe to changes scoped server-side to this shop. Previous
    // version used an unscoped channel name `technicians-changes` and no
    // filter — every shop's technician events streamed to every connected
    // client and were silently discarded after a full refetch. Now scoped
    // by shop_id so cross-tenant noise stays on the server.
    if (!activeShop?.id) return;
    const shopId = activeShop.id;
    const channel = supabase
      .channel(`technicians-changes-${shopId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'technicians',
        filter: `shop_id=eq.${shopId}`,
      }, () => {
        loadTechnicians();
      })
      .subscribe((status, err) => {
        // FIX-R36-LOW-1: status visibility on technicians realtime.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[Technicians] realtime channel status:', status, err);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeShop?.id]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen bg-background">
        <TopBar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex flex-col h-screen bg-background">
      <div className="tech-grid" />
      <div className="relative z-10 flex flex-col h-full">
      <TopBar />
      
      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Technician Management</h1>
              <p className="text-muted-foreground mt-1">Manage your shop's technician roster and skills</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/')}>
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate('/technicians/new')}>
                <Plus className="mr-2 h-4 w-4" />
                New Technician
              </Button>
            </div>
          </div>

          {technicians.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Wrench className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Technicians Yet</h3>
                <p className="text-muted-foreground mb-4">Get started by adding your first technician</p>
                <Button onClick={() => navigate('/technicians/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Technician
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {technicians.map(tech => {
                const profValues = Object.values(tech.proficiencies || {}) as number[];
                const avgProficiency = profValues.length > 0 
                  ? profValues.reduce((sum, val) => sum + val, 0) / profValues.length 
                  : 0;
                
                // Skill tier: derived from proficiencies when the tech has any,
                // otherwise the stored technicians.skill_tier. Without the
                // fallback this page rendered 1/10 for every wizard-created
                // technician (proficiencies = {}) while /shop-profile rendered
                // the stored tier for the same row.
                const calculatedSkillTier = resolveSkillTier(tech.proficiencies, tech.skill_tier);
                
                // Derive specialties from proficiencies (>60)
                const derivedSpecialties = PROFICIENCY_CATEGORIES.filter(
                  category => (tech.proficiencies?.[category] || 0) > 60
                );

                // Prefer derived hours JSON to display canonical shift
                const hoursJson = (tech as any).hours || {};
                const hoursEntries = Object.values(hoursJson) as Array<{ open?: string; close?: string }>;
                const opens = hoursEntries.map(e => e?.open).filter(Boolean) as string[];
                const closes = hoursEntries.map(e => e?.close).filter(Boolean) as string[];
                const toMinutes = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
                const earliestOpen = opens.length ? opens.reduce((a, b) => (toMinutes(a) <= toMinutes(b) ? a : b)) : tech.shift_start;
                const latestClose = closes.length ? closes.reduce((a, b) => (toMinutes(a) >= toMinutes(b) ? a : b)) : tech.shift_end;
                const fmt = (t?: string) => t ? new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : ''; 
                const displayStart = fmt(earliestOpen);
                const displayEnd = fmt(latestClose);

                return (
                  <Card key={tech.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">
                      {tech.name}
                      {tech.employment_status === 'Inactive' && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Inactive
                        </Badge>
                      )}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{tech.role}</p>
                  </div>
                  <Badge variant={tech.is_present ? 'default' : 'secondary'}>
                    {tech.is_present ? 'Present' : 'Away'}
                  </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Award className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Skill Tier:</span>
                        <span className="font-medium">{calculatedSkillTier}/10</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Shift:</span>
                        <span className="font-medium">{displayStart} - {displayEnd}</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground" title={EFFICIENCY_NAMES.planning.scope}>{EFFICIENCY_NAMES.planning.label}:</span>
                          <span className="ml-1 font-medium">{tech.efficiency_percentage || 100}%</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Buffer:</span>
                          <span className="ml-1 font-medium">{formatPlanningBufferLabel(tech.buffer_percentage)}</span>
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Bay Assignments:</p>
                        <div className="flex flex-wrap gap-1">
                          {tech.bay_assignments?.map((bay: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {bay}
                            </Badge>
                          )) || <span className="text-xs text-muted-foreground">None</span>}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Specialties:</p>
                        <div className="flex flex-wrap gap-1">
                          {derivedSpecialties.map((specialty: string, idx: number) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {specialty}
                            </Badge>
                          )) || <span className="text-xs text-muted-foreground">None</span>}
                        </div>
                      </div>

                      <div className="pt-2">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Avg Proficiency</span>
                          <span className="font-medium">{Math.round(avgProficiency)}%</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(avgProficiency, 100)}%` }}
                          />
                        </div>
                      </div>

                      <Button 
                        variant="outline" 
                        className="w-full mt-2"
                        onClick={() => navigate(`/technicians/${tech.id}/edit`)}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit Details
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      </div>
    </div>
  );
};

export default Technicians;
